import { useSelector } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AuthenticateRoute from "./AuthenticateRoute";
import PrivateRoute from "./PrivateRoute";
import Layout from "../layouts/index";
import Login from "../pages/Login.jsx";
import Welcome from "../pages/Welcome";
import Profile from "../pages/Profile";
import Unauthorised from "../pages/Unauthorised";
import ForgotPassword from "../pages/ForgotPassword.jsx";
import Register from "../pages/Register";
import Home from "../pages/Home.jsx";
import User from "../pages/user-management/user/User.jsx";
import UserAdd from "../pages/user-management/user/UserAdd.jsx";
import Role from "../pages/user-management/role/Role";
import RoleAdd from "../pages/user-management/role/RoleAddEdit";
import Associate from "../pages/user-management/associate/Associate.jsx";
import AssociateAddEdit from "../pages/user-management/associate/AssociateAddEdit.jsx";
import AssociateDetails from "../pages/user-management/associate/AssociateDetails.jsx";
import Fixer from "../pages/user-management/fixer/Fixer.jsx";
import FixerDetails from "../pages/user-management/fixer/FixerDetails.jsx";
import FormElements from "../pages/form-elements-management/FormElements";
import FormElementsAddEdit from "../pages/form-elements-management/FormElementsAddEdit";
import Screens from "../pages/screen-management/Screens";
import ScreenAddEdit from "../pages/screen-management/ScreenAddEdit";
import Pincodes from "../pages/pincode-management/Pincodes";
import PincodeAdd from "../pages/pincode-management/PincodeAdd";
import PincodeAssign from "../pages/pincode-management/PincodeAssign";
import ServiceCategories from "../pages/service-category-management/ServiceCategories";
import ServiceCategoryAddEdit from "../pages/service-category-management/ServiceCategoryAddEdit";
import PricingSettings from "../pages/pricing-management/PricingSettings";

const RouteConfig = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  return (
    <BrowserRouter>
      <Routes>
        {/* ── Public routes ── */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/unauthorised" element={<Unauthorised />} />
        <Route
          path="/welcome"
          element={
            <Layout>
              <Welcome />
            </Layout>
          }
        />

        {/* ── Private routes — all share a single Layout ── */}
        <Route element={<PrivateRoute isAllowed={isLoggedIn} />}>
          <Route element={<Layout />}>
            <Route path="profile" element={<Profile />} />

            <Route
              path="dashboard"
              element={
                <AuthenticateRoute
                  permission="Permissions.Dashboard.View"
                  redirectPath="/welcome"
                >
                  <Home />
                </AuthenticateRoute>
              }
            />

            {/* User management */}
            <Route
              path="users"
              element={
                <AuthenticateRoute permission="Permissions.User.View">
                  <User />
                </AuthenticateRoute>
              }
            />
            <Route path="users/add" element={<UserAdd />} />
            <Route path="users/edit/:id" element={<UserAdd />} />
            <Route path="roles" element={<Role />} />
            <Route path="role-add" element={<RoleAdd />} />
            <Route path="role-edit/:id" element={<RoleAdd />} />

            {/* Associate management */}
            <Route
              path="associates"
              element={
                <AuthenticateRoute permission="Permissions.Associate.View">
                  <Associate />
                </AuthenticateRoute>
              }
            />
            <Route path="associates/add" element={<AssociateAddEdit />} />
            <Route path="associates/edit/:id" element={<AssociateAddEdit />} />
            <Route path="associates/details/:id" element={<AssociateDetails />} />

            {/* Fixer management */}
            <Route
              path="fixers"
              element={
                <AuthenticateRoute permission="Permissions.Fixer.View">
                  <Fixer />
                </AuthenticateRoute>
              }
            />
            <Route
              path="fixers/details/:id"
              element={
                <AuthenticateRoute permission="Permissions.Fixer.View">
                  <FixerDetails />
                </AuthenticateRoute>
              }
            />

            {/* Screen management */}
            <Route
              path="screen"
              element={
                <AuthenticateRoute permission="Permissions.Screen.View">
                  <Screens />
                </AuthenticateRoute>
              }
            />
            <Route
              path="screen/add"
              element={
                <AuthenticateRoute permission="Permissions.Screen.Add">
                  <ScreenAddEdit />
                </AuthenticateRoute>
              }
            />
            <Route
              path="screen/edit/:id"
              element={
                <AuthenticateRoute permission="Permissions.Screen.Update">
                  <ScreenAddEdit />
                </AuthenticateRoute>
              }
            />

            {/* Pincode management */}
            <Route
              path="pincodes"
              element={
                <AuthenticateRoute permission="Permissions.Pincode.View">
                  <Pincodes />
                </AuthenticateRoute>
              }
            />
            <Route
              path="pincodes/add"
              element={
                <AuthenticateRoute permission="Permissions.Pincode.Add">
                  <PincodeAdd />
                </AuthenticateRoute>
              }
            />
            <Route
              path="pincodes/assign"
              element={
                <AuthenticateRoute permission="Permissions.Pincode.Update">
                  <PincodeAssign />
                </AuthenticateRoute>
              }
            />

            {/* Service Category management */}
            <Route
              path="service-categories"
              element={
                <AuthenticateRoute permission="Permissions.ServiceCategory.View">
                  <ServiceCategories />
                </AuthenticateRoute>
              }
            />
            <Route
              path="service-categories/add"
              element={
                <AuthenticateRoute permission="Permissions.ServiceCategory.Add">
                  <ServiceCategoryAddEdit />
                </AuthenticateRoute>
              }
            />
            <Route
              path="service-categories/edit/:id"
              element={
                <AuthenticateRoute permission="Permissions.ServiceCategory.Update">
                  <ServiceCategoryAddEdit />
                </AuthenticateRoute>
              }
            />

            {/* Pricing management */}
            <Route
              path="pricing"
              element={
                <AuthenticateRoute permission="Permissions.Pricing.View">
                  <PricingSettings />
                </AuthenticateRoute>
              }
            />

            {/* Form elements management */}
            <Route
              path="form-elements"
              element={
                <AuthenticateRoute permission="Permissions.FormElement.View">
                  <FormElements />
                </AuthenticateRoute>
              }
            />
            <Route
              path="form-elements/add"
              element={
                <AuthenticateRoute permission="Permissions.FormElement.Add">
                  <FormElementsAddEdit />
                </AuthenticateRoute>
              }
            />
            <Route
              path="form-elements/edit/:id"
              element={
                <AuthenticateRoute permission="Permissions.FormElement.Update">
                  <FormElementsAddEdit />
                </AuthenticateRoute>
              }
            />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default RouteConfig;
